import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Sparkles, X } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { Button, useToast } from '../ui/index.js';

interface Proofed {
  text: string;
  changes: string[];
  unchanged: boolean;
}

/**
 * AI Proof for a reply or note box: ask for a tightened, corrected version
 * of the draft, then use it or keep your own.
 *
 * The suggestion is shown under the box, never swapped in by itself -- the
 * agent is the author, and the model is an editor who has to be agreed with.
 */
export function useAiProof(tenant: string, number: string | number) {
  const toast = useToast();
  const [result, setResult] = useState<(Proofed & { from: string }) | null>(null);
  const run = useMutation({
    mutationFn: (v: { text: string; kind: 'reply' | 'note' }) =>
      api.post<Proofed>(`/api/v1/tenants/${tenant}/tickets/${number}/proof`, v),
    onSuccess: (r, v) => {
      if (r.unchanged) {
        toast.success('Looks good as it is', 'Nothing worth changing.');
        setResult(null);
        return;
      }
      setResult({ ...r, from: v.text });
    },
    onError: (e) => toast.error('AI Proof did not run', e instanceof ApiError ? e.message : undefined),
  });
  return { run, result, clear: () => setResult(null) };
}

export function AiProofButton({
  onClick,
  pending,
  disabled,
}: {
  onClick: () => void;
  pending: boolean;
  disabled: boolean;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      leading={<Sparkles className="h-3.5 w-3.5" />}
      loading={pending}
      disabled={disabled}
      onClick={onClick}
      title="Tighten and correct this draft without changing what it says"
    >
      AI Proof
    </Button>
  );
}

export function AiProofResult({
  result,
  current,
  onUse,
  onKeep,
}: {
  result: Proofed & { from: string };
  current: string;
  onUse: (text: string) => void;
  onKeep: () => void;
}) {
  // Edited since it was proofread: the suggestion is about a different text.
  const stale = current !== result.from;
  return (
    <div className="mt-2.5 rounded-md border border-brand-line/60 bg-brand-tint/40 p-2.5">
      <p className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
        <Sparkles aria-hidden className="h-3 w-3" /> Suggested
      </p>
      <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-ink">{result.text}</p>
      {result.changes.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-2xs text-ink-secondary">
          {result.changes.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      )}
      {stale && <p className="mt-1.5 text-2xs text-warn">You have changed the draft since — using this replaces those changes.</p>}
      <div className="mt-2 flex gap-1.5">
        <Button size="xs" leading={<Check />} onClick={() => onUse(result.text)}>
          Use it
        </Button>
        <Button size="xs" variant="ghost" leading={<X />} onClick={onKeep}>
          Keep mine
        </Button>
      </div>
    </div>
  );
}
